/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, uuid, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { products } from "./products";

/**
 * Contact organization types - represents different kinds of external organizations
 * that a product team interacts with
 */
export const CONTACT_ORG_TYPES = {
  CUSTOMER: 'customer',     // Customer organizations
  PARTNER: 'partner',       // Partner organizations
  INTERNAL: 'internal',     // Internal teams within the company
  VENDOR: 'vendor',         // Vendor/supplier organizations
  OTHER: 'other',           // Other organization types
} as const;

export type ContactOrgType = typeof CONTACT_ORG_TYPES[keyof typeof CONTACT_ORG_TYPES];

/**
 * Contact Organizations table - stores external organizations associated with a product
 * These are different from platform organizations - they represent customers, partners,
 * internal teams, vendors, etc. that the product team interacts with.
 */
export const contactOrganizations = pgTable("contact_organizations", {
  id: uuid("id").primaryKey().defaultRandom(),
  productId: uuid("product_id").notNull().references(() => products.id, { onDelete: 'cascade' }),
  name: text("name").notNull(),
  orgType: text("org_type").notNull().default('other'), // customer, partner, internal, vendor, other
  description: text("description"),
  website: text("website"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertContactOrganizationSchema = createInsertSchema(contactOrganizations).omit({
  createdAt: true,
  updatedAt: true,
}).extend({
  id: z.string().uuid().optional(),
  orgType: z.enum(['customer', 'partner', 'internal', 'vendor', 'other']).default('other'),
});

export const updateContactOrganizationSchema = insertContactOrganizationSchema.partial().extend({
  id: z.string().uuid(),
});

export type ContactOrganization = typeof contactOrganizations.$inferSelect;
export type InsertContactOrganization = z.infer<typeof insertContactOrganizationSchema>;
export type UpdateContactOrganization = z.infer<typeof updateContactOrganizationSchema>;

/**
 * Contact Teams table - teams within a contact organization
 * Allows grouping contacts by department, team, or function within an organization
 */
export const contactTeams = pgTable("contact_teams", {
  id: uuid("id").primaryKey().defaultRandom(),
  organizationId: uuid("organization_id").notNull().references(() => contactOrganizations.id, { onDelete: 'cascade' }),
  name: text("name").notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertContactTeamSchema = createInsertSchema(contactTeams).omit({
  createdAt: true,
  updatedAt: true,
}).extend({
  id: z.string().uuid().optional(),
});

export const updateContactTeamSchema = insertContactTeamSchema.partial().extend({
  id: z.string().uuid(),
});

export type ContactTeam = typeof contactTeams.$inferSelect;
export type InsertContactTeam = z.infer<typeof insertContactTeamSchema>;
export type UpdateContactTeam = z.infer<typeof updateContactTeamSchema>;

/**
 * Contacts table - individual contacts associated with a product
 * Each contact belongs to a contact organization and optionally to a team
 */
export const contacts = pgTable("contacts", {
  id: uuid("id").primaryKey().defaultRandom(),
  organizationId: uuid("organization_id").notNull().references(() => contactOrganizations.id, { onDelete: 'cascade' }),
  teamId: uuid("team_id").references(() => contactTeams.id, { onDelete: 'set null' }),
  name: text("name").notNull(),
  email: text("email"),
  phone: text("phone"),
  role: text("role"), // Job title or role
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertContactSchema = createInsertSchema(contacts).omit({
  createdAt: true,
  updatedAt: true,
}).extend({
  id: z.string().uuid().optional(),
  teamId: z.string().uuid().optional().nullable(),
  email: z.string().email().optional().nullable(),
});

export const updateContactSchema = insertContactSchema.partial().extend({
  id: z.string().uuid(),
});

export type Contact = typeof contacts.$inferSelect;
export type InsertContact = z.infer<typeof insertContactSchema>;
export type UpdateContact = z.infer<typeof updateContactSchema>;

/**
 * Extended type that includes organization and team details
 */
export interface ContactWithDetails extends Contact {
  organization?: ContactOrganization;
  team?: ContactTeam | null;
}

/**
 * Extended type for organization with teams and contacts count
 */
export interface ContactOrganizationWithDetails extends ContactOrganization {
  teams?: ContactTeam[];
  contactsCount?: number;
}
